import { useCallback, useRef, useState } from 'react'

import {
  type DocumentText,
  type LibraryDocument,
  changeMarkup,
  discardOwnLine,
  fetchText,
  isOutOfDate,
  saveText,
} from '../../api/library'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { RichText } from '../../ui/RichText'
import { Done, Failed, Waiting } from '../../ui/Status'
import { useSaving } from '../../ui/useSaving'

/**
 * A document, open for writing.
 *
 * The same editor the notepad uses, with the things a *document* needs around it and nothing the notepad
 * needs. Three of them are the substance of this screen.
 *
 * **The original stays reachable.** A document that arrived from somewhere and has been edited here shows
 * the writing; one control shows the file as it arrived, in whatever state its source has since reached,
 * and another throws the writing away and goes back to it. Neither is destructive by accident: switching
 * to the original is read-only, and discarding is the one thing here that asks.
 *
 * **One session is one version.** The first save after opening adds a version to the history; every
 * automatic save after it writes into that same version. So a morning's work is one readable state rather
 * than three hundred, and yesterday's is still there beside it.
 *
 * **A save that is out of date makes the editor read-only and says so.** That happens when the same
 * document is open in another tab, or a sync brought a newer file. What was typed **stays on the screen,
 * selectable and copyable** — it is never thrown away and never silently written over somebody else's
 * newer state.
 */
export interface WriteDocumentProps {
  readonly document: LibraryDocument
  /** Called after anything that changes what the library shows, so the shelf and the tile follow. */
  readonly onChanged: () => void
  /** Whether the editor has the frame to itself, and how to ask for that. Handed to the surface. */
  readonly full?: boolean
  readonly onFull?: (full: boolean) => void
}

export function WriteDocument({ document, onChanged, full, onFull }: WriteDocumentProps) {
  const announce = useAnnounce()

  /** `null` while the writing is shown; the source version's id while the original is. */
  const [showingOriginal, setShowingOriginal] = useState<string | null>(null)

  const opened = useLoad(
    useCallback(
      (signal: AbortSignal) => fetchText(document.id, showingOriginal ?? undefined, signal),
      [document.id, showingOriginal],
    ),
  )

  if (opened.load.status === 'waiting') {
    return <Waiting label="Opening the text." />
  }

  if (opened.load.status === 'failed') {
    return (
      <Failed failure={opened.load.failure} onRetry={opened.retry} title="The text did not open" />
    )
  }

  return (
    <div className="writing">
      <Writing
        /*
          A different text is a different thing being written in, not the same editor with other words
          in it — so it is a different component. That is also what makes the state below something
          that is *created* from the text rather than kept in step with it: there is nothing to keep in
          step, because a new text means a new editor.
        */
        key={`${opened.load.value.versionId}-${opened.load.value.basedOn}`}
        document={document}
        opened={opened.load.value}
        readOnly={showingOriginal !== null}
        onChanged={onChanged}
        onRefresh={opened.retry}
        full={full}
        onFull={onFull}
      />

      {/*
        Only where there is an original to go back to. A document written here has none, and offering
        the control anyway would be offering something that cannot happen.
      */}
      {document.sourceVersionId !== null && (
        <div className="writing__original">
          <Button
            tone="quiet"
            aria-pressed={showingOriginal !== null}
            onClick={() => {
              setShowingOriginal(
                showingOriginal === null ? (document.sourceVersionId ?? null) : null,
              )
            }}
          >
            {showingOriginal === null ? 'Show the original' : 'Back to my version'}
          </Button>
          {document.written && (
            <Discard document={document} onChanged={onChanged} onDone={announce} />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * The editor itself, on one text that has already arrived.
 *
 * Everything it holds is worked out once, from the text it was given: what is on the screen, what that
 * is based on, and whether the next save opens a version. It is mounted afresh whenever the text
 * changes, which is what makes all three simply *initial values* rather than three things to keep in
 * step with a request that may still be in flight.
 */
function Writing({
  document,
  opened,
  readOnly: shown,
  onChanged,
  onRefresh,
  full,
  onFull,
}: {
  readonly document: LibraryDocument
  readonly opened: DocumentText
  /** Set while the original is being shown: it is somebody else's file and is looked at, not edited. */
  readonly readOnly: boolean
  readonly onChanged: () => void
  readonly onRefresh: () => void
  readonly full?: boolean
  readonly onFull?: (full: boolean) => void
}) {
  const [text, setText] = useState(opened.text)
  /** What the text on the screen is based on. Sent with every save; a stale one refuses the save. */
  const basedOn = useRef(opened.basedOn)
  /** Whether the next save opens a new version. True until the first save of this session has gone. */
  const beginsASession = useRef(true)

  const [saved, setSaved] = useState<number | null>(null)
  const [failure, setFailure] = useState<Failure | null>(null)
  const [outOfDate, setOutOfDate] = useState(false)

  const readOnly = shown || outOfDate

  const saving = useSaving(async (wanted) => {
    if (readOnly) {
      return
    }
    try {
      const result = await saveText(document.id, wanted, basedOn.current, beginsASession.current)
      basedOn.current = result.basedOn
      beginsASession.current = false
      setSaved(Date.now())
      setFailure(null)
      onChanged()
    } catch (error) {
      const what = asFailure(error)
      setFailure(what)
      if (isOutOfDate(what)) {
        // Read-only from here on, and **nothing is thrown away**: what was typed is still on the
        // screen, still selectable, still copyable. Refreshing is a decision, not something that
        // happens to somebody.
        setOutOfDate(true)
      }
    }
  })

  return (
    <>
      {outOfDate && (
        <div className="notice notice--bad" role="alert">
          <h3 className="notice__title">This document has changed elsewhere</h3>
          <p className="notice__text">
            Another tab, or a sync, wrote a newer version while this one was open. Nothing you typed
            has been lost — it is still here, and you can copy it out. Refresh to carry on from the
            newer state.
          </p>
          <div className="controls controls--tight">
            <Button tone="quiet" onClick={onRefresh}>
              Refresh from the newer version
            </Button>
          </div>
        </div>
      )}

      {failure !== null && !outOfDate && <Failed failure={failure} />}

      {opened.readAsFallbackEncoding && (
        <p className="sheet__aside">
          This file was not written in UTF-8 and has been read as Latin-1. Saving it here writes
          UTF-8.
        </p>
      )}

      <RichText
        text={text}
        format={opened.mediaType}
        label={document.title}
        readOnly={readOnly}
        /*
          Switching the markup changes nothing about the text, so the editor carries on exactly where
          it was — no refresh, no lost keystroke. It is read-only only while the *original* is being
          shown: that is somebody else's file.
        */
        onFormat={async (wanted) => {
          await changeMarkup(document.id, wanted)
          onChanged()
          // Read back, or nothing on this screen would change: the format the editor highlights by
          // and the preview draws with comes from the text that was fetched. It is the same text and
          // the same version, so the editor is not rebuilt and nothing typed is disturbed — only the
          // language it is shown in.
          onRefresh()
        }}
        full={full}
        onFull={onFull}
        /*
          A document is a thing that ends up on paper — handed in, read on the train, marked up with a
          pen. So the control is here, and ⌘P here prints the drawn text whole rather than photographing
          the pane it is scrolling inside.
        */
        printable
        purpose="document"
        /*
          A document that already says something is opened to be *read*: the drawn text, not the
          markup it is made of. One that is still empty has nothing to read, so it opens where the
          typing goes. Either way this is only what happens before anybody has chosen — a choice
          somebody made wins, and it is remembered apart from the notepad's.
        */
        initially={opened.text.trim() === '' ? 'text' : 'preview'}
        placeholder="Write. It saves itself."
        onChange={(wanted) => {
          setText(wanted)
          if (!readOnly) {
            saving.typed(wanted)
          }
        }}
      >
        <p className="surface__state">
          {shown ? (
            <span>The file as it arrived — read only</span>
          ) : outOfDate ? (
            <span>Read only</span>
          ) : (
            <span>Saved as you write</span>
          )}
          {saved !== null && !shown && <Done key={saved} label="Saved" />}
        </p>
      </RichText>
    </>
  )
}

/**
 * Throwing the writing away — the one thing on this screen that asks first.
 *
 * Everything else in MyLib saves itself and is undone with another control; this is undone with nothing,
 * because the versions being removed are removed. That is the same line the library draws at emptying the
 * trash, and it is drawn in the same place: at whatever cannot be taken back.
 */
function Discard({
  document,
  onChanged,
  onDone,
}: {
  readonly document: LibraryDocument
  readonly onChanged: () => void
  readonly onDone: (said: string) => void
}) {
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)

  if (!asking) {
    return (
      <Button
        tone="bare"
        className="control--danger"
        onClick={() => {
          setAsking(true)
        }}
      >
        Throw my version away
      </Button>
    )
  }

  return (
    <div className="writing__asking">
      <p className="sheet__aside">
        Everything you wrote here goes, and the document is the file it arrived as again. This
        cannot be undone.
      </p>
      {failure !== null && <Failed failure={failure} />}
      <div className="controls controls--tight">
        <Button
          tone="bare"
          className="control--danger"
          onClick={() => {
            void (async () => {
              try {
                await discardOwnLine(document.id)
                onChanged()
                onDone('Your version is gone. The original is back.')
              } catch (error) {
                setFailure(asFailure(error))
              }
            })()
          }}
        >
          Throw it away
        </Button>
        <Button
          tone="quiet"
          onClick={() => {
            setAsking(false)
          }}
        >
          Keep it
        </Button>
      </div>
    </div>
  )
}
